import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Card, Space, Button, Breadcrumb, Tag, Tooltip, Image } from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { showConfirmModal } from '../components/PopupModal';
import PopupPreviewModal from '../components/PopupPreviewModal';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import popupGuidesService from '../services/popupGuides';
import type {
  PopupGuide,
  PopupGuideApiImage,
  PopupGuideUploadedImage,
} from '../types/popup';
import { message } from '../utils/message';

const EXPOSURE_TEXT: Record<string, string> = {
  show: '노출',
  hide: '비노출',
};

const STATUS_TEXT: Record<string, string> = {
  draft: '임시 저장',
  published: '발행',
  archived: '보관',
};


const { Title, Text } = Typography;

type DisplayImage = {
  id: string;
  url: string;
  type: 'api' | 'upload';
  title?: string;
};

const PopupDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id: popupId } = useParams();
  const [loading, setLoading] = useState(false);
  const [popupData, setPopupData] = useState<PopupGuide | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [colorSchemes, setColorSchemes] = useState<Array<{ key: string; displayName: string; colors: string[] }>>([]);
  const [previewVisible, setPreviewVisible] = useState(false);

  const formatDate = (value?: string | null) => (value ? dayjs(value).format('YYYY.MM.DD') : '-');
  const formatDateTime = (value?: string | null) => (value ? dayjs(value).format('YYYY.MM.DD HH:mm') : '-');

  const displayImages = useMemo<DisplayImage[]>(() => {
    if (!popupData) {
      return [];
    }

    const apiImages = popupData.apiImages ?? [];
    const uploadedImages = popupData.uploadedImages ?? [];
    const selectedIds = popupData.selectedImageIds ?? [];

    const normalizeApiImage = (image: PopupGuideApiImage): DisplayImage | null => {
      const url = image.url || image.thumbnail || image.originalUrl;
      if (!url) {
        return null;
      }

      return {
        id: image.id,
        url,
        type: 'api',
        title: image.title,
      };
    };

    const normalizeUploadedImage = (image: PopupGuideUploadedImage): DisplayImage | null => {
      if (!image.url) {
        return null;
      }

      return {
        id: `upload:${image.filename}`,
        url: image.url,
        type: 'upload',
        title: image.originalName || image.filename,
      };
    };

    const apiMap = new Map(apiImages.map((image) => [image.id, image]));
    const uploadMap = new Map<string, PopupGuideUploadedImage>();
    uploadedImages.forEach((image) => {
      uploadMap.set(`upload:${image.filename}`, image);
      uploadMap.set(image.filename, image);
    });

    const result: DisplayImage[] = [];

    if (!selectedIds.length) {
      apiImages.forEach((image) => {
        const normalized = normalizeApiImage(image);
        if (normalized) {
          result.push(normalized);
        }
      });

      uploadedImages.forEach((image) => {
        const normalized = normalizeUploadedImage(image);
        if (normalized) {
          result.push(normalized);
        }
      });

      return result;
    }

    selectedIds.forEach((rawId) => {
      const apiCandidate = apiMap.get(rawId);
      if (apiCandidate) {
        const normalized = normalizeApiImage(apiCandidate);
        if (normalized) {
          result.push(normalized);
        }
        return;
      }

      // API 이미지가 apiImages 배열에서 찾을 수 없는 경우 폴백 처리
      if (rawId.startsWith('api:')) {
        const fallbackUrl = rawId.replace('api:', '');
        if (fallbackUrl && fallbackUrl.startsWith('http')) {
          result.push({
            id: rawId,
            url: fallbackUrl,
            type: 'api',
            title: '외부 이미지 (원본 링크)',
          } as DisplayImage);
        }
        return;
      }

      const normalizedId = rawId.startsWith('upload:')
        ? rawId
        : `upload:${rawId.replace(/^upload[:_]/, '')}`;

      const uploadCandidate = uploadMap.get(rawId) || uploadMap.get(normalizedId);
      if (uploadCandidate) {
        const normalized = normalizeUploadedImage(uploadCandidate);
        if (normalized) {
          result.push(normalized);
        }
      }
    });

    if (!result.length) {
      apiImages.forEach((image) => {
        const normalized = normalizeApiImage(image);
        if (normalized) {
          result.push(normalized);
        }
      });

      uploadedImages.forEach((image) => {
        const normalized = normalizeUploadedImage(image);
        if (normalized) {
          result.push(normalized);
        }
      });
    }

    return result;
  }, [popupData]);

  // 컬러 스킴 로드
  useEffect(() => {
    const loadColorSchemes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cms/color-schemes/active`);
        const data = await response.json();
        if (data.success) {
          setColorSchemes(data.data as Array<{ key: string; displayName: string; colors: string[] }>);
        }
      } catch (error) {
        console.error('Failed to load color schemes:', error);
        setColorSchemes([]);
      }
    };

    loadColorSchemes();
  }, []);

  // 데이터 로드
  useEffect(() => {
    const loadPopupData = async () => {
      if (!popupId) {
        message.error('팝업 가이드 ID가 필요합니다.');
        navigate('/popups');
        return;
      }

      setLoading(true);
      try {
        const data = await popupGuidesService.getGuide(popupId);
        setPopupData(data);
      } catch (error: any) {
        console.error('Load popup data error:', error);
        const errorMessage = error?.response?.data?.message || '팝업 정보를 불러오는 중 오류가 발생했습니다.';
        message.error(errorMessage);
        navigate('/popups');
      } finally {
        setLoading(false);
      }
    };

    loadPopupData();
  }, [popupId, navigate]);

  // 뒤로가기 핸들러
  const handleBack = () => {
    navigate('/popups');
  };

  // 수정 핸들러
  const handleEdit = () => {
    navigate(`/popups/edit/${popupId}`);
  };

  // 삭제 핸들러
  const handleDelete = () => {
    if (!popupId) {
      message.error('유효하지 않은 팝업 가이드입니다.');
      return;
    }

    showConfirmModal({
      title: '삭제',
      content: '삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?',
      okText: '확인',
      okType: 'danger',
      cancelText: '취소',
      onConfirm: async () => {
        try {
          setDeleteLoading(true);
          await popupGuidesService.deleteGuide(popupId);
          message.success('삭제가 완료되었습니다.');
          navigate('/popups');
        } catch (error: any) {
          console.error('삭제 실패:', error);
          const errorMessage = error?.response?.data?.message || '삭제 중 오류가 발생했습니다.';
          message.error(errorMessage);
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  // 미리보기 핸들러
  const handlePreview = () => {
    if (!popupData) {
      message.warning('팝업 데이터가 없습니다.');
      return;
    }

    if (displayImages.length === 0) {
      message.warning('미리보기할 이미지가 없습니다.');
      return;
    }

    setPreviewVisible(true);
  };

  // 미리보기 모달 닫기 핸들러
  const handlePreviewClose = () => {
    setPreviewVisible(false);
  };

  // 이미지 전체 다운로드 핸들러
  const handleDownloadAllImages = async () => {
    if (!displayImages.length) {
      message.warning('다운로드할 이미지가 없습니다.');
      return;
    }

    if (!popupId) {
      message.error('팝업 가이드 ID가 필요합니다.');
      return;
    }

    try {
      message.loading({ content: '이미지 다운로드 중...', key: 'download', duration: 30 });

      // 백엔드 API 호출
      const blob = await popupGuidesService.downloadImagesZip(popupId);

      // 파일 다운로드
      const fileName = `${popupData?.storeName || 'popup'}_images_${dayjs().format('YYYYMMDD_HHmmss')}.zip`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({ content: '이미지가 다운로드되었습니다.', key: 'download', duration: 2 });
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.response?.data?.message || '이미지 다운로드 중 오류가 발생했습니다.';
      message.error({ content: errorMessage, key: 'download', duration: 2 });
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '0', textAlign: 'center', marginTop: '100px' }}>
        <Text style={{ color: '#ffffff' }}>로딩 중...</Text>
      </div>
    );
  }

  if (!popupData) {
    return (
      <div style={{ padding: '0', textAlign: 'center', marginTop: '100px' }}>
        <Text style={{ color: '#ffffff' }}>데이터를 찾을 수 없습니다.</Text>
      </div>
    );
  }

  const currentScheme = colorSchemes.find(scheme => scheme.key === popupData.colorScheme);
  const colorPalette = currentScheme?.colors || [];
  const exposureLabel = EXPOSURE_TEXT[popupData.exposure] || popupData.exposure;
  const statusLabel = STATUS_TEXT[popupData.status] || popupData.status;
  const hashtags = popupData.hashtags || [];

  // 노출 기간: startDate와 endDate가 모두 null이면 "상시노출"
  const exposurePeriodLabel = (!popupData.startDate && !popupData.endDate)
    ? '상시노출'
    : `${formatDate(popupData.startDate)} ~ ${formatDate(popupData.endDate)}`;

  // 팝업 기간: popupStartDate와 popupEndDate가 모두 null이면 "상시노출"
  const popupPeriodLabel = (!popupData.popupStartDate && !popupData.popupEndDate)
    ? '상시노출'
    : `${formatDate(popupData.popupStartDate)} ~ ${formatDate(popupData.popupEndDate)}`;

  const exposureTagColor = popupData.exposure === 'show' ? 'green' : 'volcano';
  const statusTagColor = popupData.status === 'published' ? 'blue' : (popupData.status === 'archived' ? 'orange' : undefined);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '라이프&컬쳐' },
          { title: '팝업 가이드', href: '/popups' },
          { title: '팝업 가이드 상세' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0 0 32px 0',
      }}>
        <Title level={2} style={{ color: '#ffffff', margin: 0 }}>
          {popupData.guideName || popupData.layoutName}
        </Title>
        <Button onClick={handlePreview}>
          미리보기
        </Button>
      </div>

      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>기본 정보</Text>}
        style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              레이아웃명
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{popupData.layoutName}</Text>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              레이아웃 색상
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Tooltip
                title={
                  colorPalette.length > 0 ? (
                    <div style={{ fontSize: '12px', textAlign: 'left' }}>
                      <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>{currentScheme?.displayName || '-'}</div>
                      <div>Primary : {colorPalette[0]?.toUpperCase() || '-'}</div>
                      <div>Secondary : {colorPalette[1]?.toUpperCase() || '-'}</div>
                    </div>
                  ) : null
                }
                placement="top"
                color="#000000"
              >
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  {colorPalette.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                      {/* Primary 컬러 (앞쪽) */}
                      <div
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          border: '0px solid #ffffff',
                          backgroundColor: colorPalette[0],
                          position: 'relative',
                          zIndex: 2,
                        }}
                      />
                      {/* Second 컬러 (뒤쪽, 겹침) */}
                      {colorPalette[1] && (
                        <div
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            border: '0px solid #ffffff',
                            backgroundColor: colorPalette[1],
                            position: 'relative',
                            marginLeft: '-22px',
                            zIndex: 1,
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <Text style={{ color: '#a6a6a6', fontSize: '13px' }}>색상 정보 없음</Text>
                  )}
                </div>
              </Tooltip>
            </div>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              노출여부
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{exposureLabel}</Text>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              노출 기간
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {exposurePeriodLabel}
            </Text>
          </div>
        </div>
      </Card>

      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>팝업 스토어 정보</Text>}
        style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              상호명
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{popupData.storeName}</Text>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              주소
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {popupData.address}
              {popupData.detailAddress && ` ${popupData.detailAddress}`}
            </Text>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              팝업 기간
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {popupPeriodLabel}
            </Text>
          </div>
        </div>
      </Card>

      {displayImages.length > 0 && (
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>
                이미지 정보 ({displayImages.length}/6)
              </Text>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadAllImages}
                style={{ background: '#1890ff', border: 'none' }}
              >
                전체 저장
              </Button>
            </div>
          }
          style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
          styles={{ body: { padding: '24px' } }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 200px)',
            gap: '8px'
          }}>
            {displayImages.map((image: DisplayImage, index: number) => (
              <div key={image.id || index} style={{ position: 'relative' }}>
                {failedImages.has(image.url) ? (
                  <div style={{
                    width: '200px',
                    height: '250px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#262626',
                    border: '1px solid #303030',
                    borderRadius: '8px',
                    color: '#666',
                    fontSize: '12px',
                    textAlign: 'center',
                    flexDirection: 'column'
                  }}>
                    <div>🖼️</div>
                    <div style={{ marginTop: '4px' }}>이미지를 불러올 수 없습니다</div>
                  </div>
                ) : (
                  <div style={{
                    width: '200px',
                    height: '250px',
                    borderRadius: '8px',
                    border: '1px solid #303030',
                    background: '#1a1a1a',
                    overflow: 'hidden',
                    padding: 0,
                    margin: 0,
                    position: 'relative'
                  }}>
                    <Image
                      src={image.url}
                      alt={image.title || `이미지 ${index + 1}`}
                      style={{
                        width: '200px',
                        height: '250px',
                        objectFit: 'cover',
                        display: 'block',
                        margin: 0,
                        padding: 0,
                        verticalAlign: 'top'
                      }}
                      preview={{
                        mask: <div><EyeOutlined /> 미리보기</div>
                      }}
                      wrapperStyle={{
                        width: '200px',
                        height: '250px',
                        display: 'block',
                        margin: 0,
                        padding: 0,
                        lineHeight: 0
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const currentSrc = target.src;

                        // 1차: 크기 조정 파라미터 제거한 URL로 재시도
                        if (currentSrc.includes('?type=') || currentSrc.includes('?w=') || currentSrc.includes('?h=')) {
                          try {
                            const urlObj = new URL(currentSrc);
                            urlObj.searchParams.delete('type');
                            urlObj.searchParams.delete('w');
                            urlObj.searchParams.delete('h');
                            const cleanUrl = urlObj.toString();
                            if (cleanUrl !== currentSrc) {
                              target.src = cleanUrl;
                              return;
                            }
                          } catch (urlError) {
                            // URL 파싱 실패 시 계속 진행
                          }
                        }

                        // 2차: 다른 폴백 옵션이 있다면 시도
                        // 현재는 바로 실패 처리
                        setFailedImages(prev => new Set(Array.from(prev).concat(image.url)));
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>가이드 정보</Text>}
        style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              가이드명
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {popupData.guideName || popupData.layoutName || '-'}
            </Text>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              해시태그
            </Text>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {hashtags.length > 0 ? (
                hashtags.map((tag) => (
                  <Tag key={tag} color="#F24822" style={{ margin: 0 }}>
                    #{tag}
                  </Tag>
                ))
              ) : (
                <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>등록된 해시태그가 없습니다.</Text>
              )}
            </div>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              내용
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
              {popupData.description || '-'}
            </Text>
          </div>
        </div>
      </Card>

      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>등록 정보</Text>}
        style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <Text style={{ color: '#a6a6a6', fontSize: '13px', display: 'block', marginBottom: '4px' }}>등록일</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{formatDateTime(popupData.createdAt)}</Text>
          </div>
          <div>
            <Text style={{ color: '#a6a6a6', fontSize: '13px', display: 'block', marginBottom: '4px' }}>최근 수정일</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{formatDateTime(popupData.updatedAt)}</Text>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px' }}>
        {/* 좌하단 삭제 버튼 */}
        <Button
          danger
          size="large"
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          loading={deleteLoading}
          style={{ minWidth: '100px' }}
        >
          삭제
        </Button>

        {/* 우하단 수정 버튼 */}
        <Button
          type="primary"
          size="large"
          onClick={handleEdit}
          style={{ minWidth: '100px', background: '#1890ff', border: 'none' }}
        >
          수정
        </Button>
      </div>

      {/* 미리보기 모달 */}
      <PopupPreviewModal
        visible={previewVisible}
        onClose={handlePreviewClose}
        popup={popupData}
        displayImages={displayImages}
      />
    </Space>
  );
};

export default PopupDetail;